import * as joda from "@js-joda/core";
import currency from "currency.js";
import { checkAllCasesHandled, mapPartial } from "trader.ts/utils";
import { connection } from "../../../connection";
import { getServerSetting } from "../../../serversetting";
import {
  get_invoicelines_for_lessongroup,
  get_invoices_by_id,
  get_periodfees,
  get_periodfees_for_student,
  next_invoice_external_id,
} from "../../../sql/classy/finance";
import { get_enrollments_for_student_by_ids } from "../../../sql/classy/lessons";
import { get_student_charges } from "../../../sql/classy/student_charges";
import {
  each_period_invoice_due_date,
  each_period_invoice_lines,
  each_period_invoice_pay_during_online_enrollment,
  expected_payments,
  lesson_finance_creditnotes,
} from "../../../sql/classy/tables";
import {
  enrollmentid,
  enrollmentrequestid,
  expectedpaymentid,
  formulachargemode,
  invoiceid,
  lessongroupid,
  periodic_payment_method,
  periodid,
  placeholderinvoiceid,
  price_adapted_to_lg,
  price_adapted_to_lg_and_student,
  price_formula,
  student_charge_id,
  student_period_fee_id,
  studentid,
} from "../../../sql/classy/types";
import {
  country,
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "../../domain/config";
import { currencyToNumber } from "../../lib/currency";
import { groupByIntoArrays } from "../../lib/groupbyintoarrays";
import { isEqual } from "../../lib/isequal";
import { isNil } from "../../lib/istype";
import { promiseAllFromObject } from "../../lib/promise";
import { getMonthsFromDateRange } from "../../lib/time";
import {
  invoicelinetype,
  parseInvoiceDueDateSetting,
  parseInvoicelineType,
  parseMonth,
  singleDueDateCalculation,
} from "../../parsing_from_ur";
import { renderCurrency } from "../../render";
import { classyTsContext, schoolurl } from "../../types";
import { invoiceFull, invoiceSubject } from "./types";
import { getEnrollmentrequestFull } from "../../pages/approve_enrollment_request/server";

export function adaptInvoiceLinesToStudent<
  IL extends {
    amount_with_vat: price_adapted_to_lg;
    amount_without_vat: price_adapted_to_lg | null;
    chargemode: formulachargemode;
    vat: number;
  },
>(
  country: country,
  schoolurl: schoolurl,
  birthday: joda.LocalDate | null,
  chargemode:
    | { tag: "not applicable"; firstlesson: joda.LocalDate }
    | {
        tag: "applicable";
        lessons:
          | {
              tag: "enrollmentrequest";
              numberoflessons: number;
              firstlesson: joda.LocalDate;
            }
          | {
              tag: "lessongroup";
              lessons: joda.LocalDate[];
              startedlessonson: joda.LocalDate;
            };
      },
  ils: IL[],
): (Omit<IL, "amount_with_vat" | "amount_without_vat" | "chargemode"> & {
  amount: price_adapted_to_lg_and_student;
})[] {
  return ils.map((il) =>
    adaptInvoiceLineToStudent(country, schoolurl, birthday, chargemode, il),
  );
}

export function adaptInvoiceLineToStudent<
  IL extends {
    amount_with_vat: price_adapted_to_lg;
    amount_without_vat: price_adapted_to_lg | null;
    chargemode: formulachargemode;
    vat: number;
  },
>(
  country: country,
  schoolurl: schoolurl,
  birthday: joda.LocalDate | null,
  chargemode:
    | { tag: "not applicable"; firstlesson: joda.LocalDate }
    | {
        tag: "applicable";
        lessons:
          | {
              tag: "enrollmentrequest";
              numberoflessons: number;
              firstlesson: joda.LocalDate;
            }
          | {
              tag: "lessongroup";
              lessons: joda.LocalDate[];
              startedlessonson: joda.LocalDate;
            };
      },
  il: IL,
): Omit<IL, "amount_with_vat" | "amount_without_vat" | "chargemode"> & {
  amount: price_adapted_to_lg_and_student;
} {
  const doRoundingToPoint5 = schoolurl === "https://zangenco.classy.school";

  const shouldReduceToZeroVat = shouldGetVATReducedToZero(country, {
    birthday,
    firstlesson:
      chargemode.tag === "not applicable"
        ? chargemode.firstlesson
        : chargemode.lessons.tag === "enrollmentrequest"
        ? chargemode.lessons.firstlesson
        : chargemode.lessons.lessons.orderBy((d) => d.toEpochDay()).first() ||
          joda.LocalDate.now(),
  });

  const amountWithVatReduced =
    shouldReduceToZeroVat === false
      ? il.amount_with_vat
      : shouldReduceToZeroVat === true
      ? calculateAmountWithoutVat(il)
      : checkAllCasesHandled(shouldReduceToZeroVat);

  const amountWithVatReducedAndAdaptedToChargemode =
    chargemode.tag === "not applicable" || il.chargemode === "Enrollment"
      ? amountWithVatReduced
      : il.chargemode === "Lesson"
      ? amountWithVatReduced.multiply(
          ((cm) =>
            cm.tag === "enrollmentrequest"
              ? cm.numberoflessons
              : cm.lessons
                  .orderBy((l) => l.toEpochDay())
                  .filter(
                    (l) =>
                      l.isEqual(cm.startedlessonson) ||
                      l.isAfter(cm.startedlessonson),
                  ).length || 1)(chargemode.lessons),
        )
      : checkAllCasesHandled(il.chargemode);

  const doRounding = doRoundingToPoint5 ? roundToPoint5 : roundToTwoDecimals;
  const res: ReturnType<typeof adaptInvoiceLineToStudent> = {
    ...il,
    amount: doRounding(amountWithVatReducedAndAdaptedToChargemode),
    vat: shouldReduceToZeroVat ? 0 : il.vat,
  };
  /* eslint-disable */
  delete res.amount_with_vat;
  delete res.amount_without_vat;
  delete res.chargemode;
  return res;
  /* eslint-enable */

  function roundToPoint5(n: currency): currency {
    const rounded = Math.round(currencyToNumber(n));
    if (currencyToNumber(n) - rounded > 0.25) {
      return currency(rounded).add(0.5);
    } else {
      return currency(rounded); // We dont go -0.5 since we don't want to "lose" money
    }
  }

  function roundToTwoDecimals(n: currency): currency {
    return currency(Math.round(currencyToNumber(n) * 100) / 100);
  }
}

export function shouldGetVATReducedToZero(
  country: country,
  opts: {
    birthday: null | joda.LocalDate;
    firstlesson: joda.LocalDate;
  },
) {
  const ageOnFirstLesson =
    opts.birthday === null
      ? 12 /* dummy */
      : Math.abs(opts.firstlesson.until(opts.birthday).years());

  return country === "Belgium"
    ? false
    : country === "Netherlands"
    ? ageOnFirstLesson < 21
    : checkAllCasesHandled(country);
}

export function adaptInvoiceLineToLg<
  IL extends {
    amount_with_vat: price_formula;
    amount_without_vat: price_formula | null;
  },
>(
  il: IL,
): Omit<IL, "amount_with_vat" | "amount_without_vat"> & {
  amount_with_vat: price_adapted_to_lg;
  amount_without_vat: price_adapted_to_lg | null;
} {
  // Does nothing for now, but I leave it in, maybe I'll need it later and it does nothing wrong
  const { amount_with_vat, amount_without_vat, ...newil } = il;
  return {
    ...newil,
    amount_with_vat: il.amount_with_vat as unknown as price_adapted_to_lg,
    amount_without_vat:
      il.amount_without_vat as unknown as price_adapted_to_lg | null,
  };
}

export type periodfeeNecessary =
  | null
  | {
      tag: "existing";
      id: student_period_fee_id;
      invoice: {
        id:
          | {
              tag: "invoice";
              invoiceid: invoiceid;
            }
          | {
              tag: "placeholderinvoice";
              placeholderinvoiceid: placeholderinvoiceid;
            };
        lines: {
          from_each_period_fee: true;
          amount: price_adapted_to_lg_and_student;
          vat: number;
          description: string;
        }[];
        amount_paid: currency;
      };
    }
  | {
      tag: "new";
      lines: {
        from_each_period_fee: true;
        amount: price_adapted_to_lg_and_student;
        vat: number;
        description: string;
        type_: invoicelinetype;
      }[];
    };

export type periodfeeAfterChoice =
  | null
  | {
      tag: "existing";
      id: student_period_fee_id;
      invoice: {
        id:
          | {
              tag: "invoice";
              invoiceid: invoiceid;
            }
          | {
              tag: "placeholderinvoice";
              placeholderinvoiceid: placeholderinvoiceid;
            };
        lines: {
          amount: price_adapted_to_lg_and_student;
          vat: number;
          description: string;
        }[];
        amount_paid: currency;
      };
    }
  | {
      tag: "new";
      lines: {
        amount: price_adapted_to_lg_and_student;
        vat: number;
        description: string;
        type_?: never;
      }[];
    };

export async function getPeriodfeeForStudent(
  {
    db,
    schoolurl,
    country,
  }: Pick<classyTsContext, "db" | "schoolurl" | "country">,
  opts: {
    student:
      | {
          tag: "new";
          birthday: joda.LocalDate | null;
        }
      | { tag: "existing"; id: studentid; birthday: joda.LocalDate | null };
    periodid: periodid;
    firstlesson: joda.LocalDate;
    formula: { periodfee_required: boolean };
  },
): Promise<periodfeeNecessary> {
  if (opts.formula.periodfee_required === false) {
    return null;
  }
  const existingpfs =
    opts.student.tag === "new"
      ? []
      : await get_periodfees_for_student(db, {
          studentids: [opts.student.id],
        });
  const existingPfThisPeriod = existingpfs.find(
    (pf) => pf.period.id === opts.periodid,
  );
  if (existingPfThisPeriod) {
    const inv =
      !isNil(existingPfThisPeriod.invoice.id) &&
      !isNil(existingPfThisPeriod.invoice.invoicelines) &&
      !isNil(existingPfThisPeriod.invoice.amount_paid)
        ? {
            id: {
              tag: "invoice" as const,
              invoiceid: existingPfThisPeriod.invoice.id,
            },
            lines:
              existingPfThisPeriod.invoice.invoicelines.map((il) => ({
                ...il,
                from_each_period_fee: true as const,
              })) || [],
            amount_paid: existingPfThisPeriod.invoice.amount_paid,
          }
        : !isNil(existingPfThisPeriod.placeholder_invoice.id) &&
          !isNil(existingPfThisPeriod.placeholder_invoice.invoicelines)
        ? {
            id: {
              tag: "placeholderinvoice" as const,
              placeholderinvoiceid: existingPfThisPeriod.placeholder_invoice.id,
            },
            lines:
              existingPfThisPeriod.placeholder_invoice.invoicelines.map(
                (il) => ({ ...il, from_each_period_fee: true as const }),
              ) || [],
            amount_paid: currency(0),
          }
        : null;
    if (inv === null) {
      throw new Error(
        `No invoice found for periodfee ${existingPfThisPeriod.id}`,
      );
    }
    return {
      tag: "existing",
      id: existingPfThisPeriod.id,
      invoice: inv,
    };
  } else {
    const lines = await each_period_invoice_lines.getAll(db);
    if (lines.length > 0) {
      return {
        tag: "new",
        lines: adaptInvoiceLinesToStudent(
          country,
          schoolurl,
          opts.student.birthday,
          { tag: "not applicable", firstlesson: opts.firstlesson },
          lines.map((il) => ({ ...il, chargemode: "Enrollment" })),
        ).map((l) => ({
          description: l.description,
          amount: l.amount,
          vat: l.vat,
          type_: parseInvoicelineType(l.type_),
          from_each_period_fee: true as const,
        })),
      };
    } else {
      return null;
    }
  }
}

export function calculateDueDate(
  calc: singleDueDateCalculation,
  dates: {
    firstlesson: joda.LocalDate | null;
    period_firstday: joda.LocalDate;
    created: joda.LocalDate;
  },
): joda.LocalDate {
  const due =
    calc.tag === "RelativeToFirstLesson"
      ? (dates.firstlesson || dates.created).plusDays(calc.days)
      : calc.tag === "RelativeToCreationDate"
      ? dates.created.plusDays(calc.days)
      : calc.tag === "RelativeToFirstDayOfPeriod"
      ? dates.period_firstday.plusDays(calc.days)
      : checkAllCasesHandled(calc);
  return due;
}

export function calculateExpectedPaymentForPeriodFee(
  each_period_fee_due_date: singleDueDateCalculation,
  each_period_fee_pay_during_online: boolean,
  lines: {
    amount: price_adapted_to_lg_and_student;
  }[],
  dates: {
    firstlesson: joda.LocalDate;
    lastlesson: joda.LocalDate;
    period_firstday: joda.LocalDate;
    created: joda.LocalDate;
  },
): null | {
  amount: currency;
  date: joda.LocalDate;
  pay_during_online_enrollment: boolean;
} {
  const totalEachPeriodFee = lines.reduce(
    (acc, il) => acc.add(il.amount),
    currency(0),
  );

  return totalEachPeriodFee.intValue > 0
    ? {
        amount: totalEachPeriodFee,
        date: calculateDueDate(each_period_fee_due_date, dates),
        pay_during_online_enrollment: each_period_fee_pay_during_online,
      }
    : null;
}

export async function creditInvoice(db: connection, inv: invoiceFull) {
  const externalid = await next_invoice_external_id(db, {
    year: joda.LocalDate.now().year(),
  });
  if (externalid === null) {
    throw new Error("Failed to calc invoice externalid");
  }
  const ils = inv.invoicelines || [];
  const vat =
    ils.length === 0
      ? 0
      : ils.every((il) => il.vat === ils[0].vat)
      ? ils[0].vat
      : 0;
  await lesson_finance_creditnotes.insert(db, {
    externalid: externalid.next_invoice_external_id,
    amount: inv.amount_left,
    date: joda.LocalDate.now(),
    invoiceid: inv.id,
    accountingsoftwareidentifier: null,
    vat: vat,
  });
  for (const ep of inv.expectedpayments || []) {
    await expected_payments.del(db, { id: ep.id });
  }
}

export function calculateExpectedPayments(
  payment_scheme: periodic_payment_scheme | non_periodic_payment_scheme,
  invoice: {
    amount: price_adapted_to_lg_and_student;
  }[],
  dates: {
    firstlesson: joda.LocalDate;
    lastlesson: joda.LocalDate;
    period_firstday: joda.LocalDate;
    created: joda.LocalDate;
  },
): {
  amount: currency;
  date: joda.LocalDate;
  pay_during_online_enrollment: boolean;
  periodic_payment_method: periodic_payment_method | null; // null = non-periodic
}[] {
  const totalInvoice = invoice.reduce(
    (acc, il) => acc.add(il.amount),
    currency(0),
  );
  let amountInDeposit = currency(0);
  const depositExpectedPayment = (() => {
    if (!isNil(payment_scheme.deposit)) {
      amountInDeposit = [payment_scheme.deposit.amount, totalInvoice].minBy(
        (c) => c.intValue,
      )!;
      return {
        amount: amountInDeposit,
        date: calculateDueDate(
          payment_scheme.deposit.due_date_calculation,
          dates,
        ),
        pay_during_online_enrollment:
          payment_scheme.deposit.pay_during_online_enrollment,
        periodic_payment_method: null,
      };
    } else {
      return null;
    }
  })();
  const restExpectedPayments = (() => {
    if (payment_scheme.tag === "periodic") {
      // Period = 1 ? Pay every month
      // Period = 2 ? Pay every 2 months
      // ...
      const months = (() => {
        if (payment_scheme.calculation.tag === "period") {
          const possibleMonths = getMonthsFromDateRange(
            dates.created.isAfter(dates.firstlesson)
              ? dates.created
              : dates.firstlesson,
            dates.lastlesson,
          );
          const period = payment_scheme.calculation.period;
          return possibleMonths.filter((_month, i) => {
            return i % period === 0;
          });
        } else if (payment_scheme.calculation.tag === "months") {
          const possibleMonths = getMonthsFromDateRange(
            dates.period_firstday,
            dates.period_firstday.plusMonths(11),
          );
          const monthsToFind =
            payment_scheme.calculation.months.map(parseMonth);
          return possibleMonths.filter((month) => {
            return monthsToFind.some((toFind) => isEqual(month[1], toFind));
          });
        } else {
          return checkAllCasesHandled(payment_scheme.calculation);
        }
      })();
      const amountPerMonth = totalInvoice
        .subtract(amountInDeposit)
        .divide(months.length);
      const eps = months.map((month) => ({
        amount: amountPerMonth,
        date: joda.LocalDate.of(month[0], month[1], 1),
        pay_during_online_enrollment: false,
        periodic_payment_method: payment_scheme.periodic_payment_method,
      }));

      const roundingDiff = totalInvoice
        .subtract(amountInDeposit)
        .subtract(eps.reduce((acc, ep) => ep.amount.add(acc), currency(0)));

      const last = eps.last();

      if (roundingDiff.intValue > 0 && last) {
        last.amount = last.amount.add(roundingDiff);
      }

      return eps;
    } else {
      return [
        {
          amount: totalInvoice.subtract(amountInDeposit),
          date: calculateDueDate(
            payment_scheme.rest.due_date_calculation,
            dates,
          ),
          pay_during_online_enrollment:
            payment_scheme.rest.pay_during_online_enrollment,
          periodic_payment_method: null,
        },
      ];
    }
  })();
  const expectedPayments = normalizeExpectedPayments(
    [depositExpectedPayment, ...restExpectedPayments].compact(),
  );
  const totalExpectedPayments = expectedPayments.reduce(
    (acc, p) => p.amount.add(acc),
    currency(0),
  );
  if (
    Math.abs(currencyToNumber(totalExpectedPayments.subtract(totalInvoice))) >
    0.5 /* accounting for rounding - not sure if still necessary */
  ) {
    console.log(JSON.stringify(expectedPayments));
    throw new Error(
      `Technical error: Expected payments does not equal invoice lines ${renderCurrency(
        totalExpectedPayments,
      )} vs ${renderCurrency(totalInvoice)}`,
    );
  }
  return expectedPayments;
}

export function getExtraCostInvoiceLine(
  paymentScheme: non_periodic_payment_scheme | periodic_payment_scheme,
): null | {
  type_: "Normal";
  amount_with_vat: price_adapted_to_lg;
  amount_without_vat: null;
  vat: number;
  description: string;
  from_each_period_fee: boolean;
  chargemode: formulachargemode;
} {
  return paymentScheme.tag === "periodic" && paymentScheme.extra_cost !== null
    ? {
        type_: "Normal",
        amount_with_vat: paymentScheme.extra_cost.amount,
        amount_without_vat: null, // not supported to specify here
        vat: paymentScheme.extra_cost.vat,
        description: "Toeslag gespreide betaling",
        from_each_period_fee: false,
        chargemode: "Enrollment",
      }
    : null;
}

export function normalizeExpectedPayments<
  EP extends {
    amount: currency;
    date: joda.LocalDate;
    pay_during_online_enrollment: boolean;
  },
>(eps: EP[]): EP[] {
  const grouped = groupByIntoArrays(
    eps,
    (t) => t.date,
    (d1, d2) => d1.isEqual(d2),
  );
  const afterGrouping = grouped
    .map(([date, group]) => ({
      ...group[0],
      pay_during_online_enrollment: group.reduce(
        (acc, il) => acc || il.pay_during_online_enrollment,
        false,
      ),
      date,
      amount: group.reduce((acc, ep) => acc.add(ep.amount), currency(0)),
    }))
    .filter((ep) => ep.amount.intValue !== 0);

  // We don't want due dates of expected payments that need to be paid
  //   during enrollment to be LATER than non-online payments
  // Otherwise the payment will be assigned to the non-online payments,
  //   as this is done by sorting on date
  const latestOnlineDueDate =
    afterGrouping
      .filter((ep) => ep.pay_during_online_enrollment === false)
      .map((ep) => ep.date)
      .minBy((d) => d.toEpochDay())
      ?.minusDays(1) || null;

  const afterOnlineDueDateFix =
    latestOnlineDueDate === null
      ? afterGrouping
      : afterGrouping.map((ep) => {
          if (ep.pay_during_online_enrollment === true) {
            if (ep.date.isAfter(latestOnlineDueDate)) {
              return {
                ...ep,
                date: latestOnlineDueDate,
              };
            } else {
              return ep;
            }
          } else {
            return ep;
          }
        });

  return afterOnlineDueDateFix;
}

export function getEachPeriodFeePaymentSettings(db: connection): Promise<{
  each_period_fee_due_date: singleDueDateCalculation;
  each_period_fee_pay_during_online: boolean;
}> {
  return promiseAllFromObject({
    each_period_fee_due_date: getServerSetting(
      db,
      each_period_invoice_due_date,
      null,
    ).then((dd) =>
      dd === null
        ? { tag: "RelativeToCreationDate", days: 14 }
        : parseInvoiceDueDateSetting(dd),
    ),
    each_period_fee_pay_during_online: getServerSetting(
      db,
      each_period_invoice_pay_during_online_enrollment,
      false,
    ),
  });
}

export async function updateExpectedPayments(
  db: connection,
  opts: {
    currentInvoice: {
      id: invoiceid;
      expectedpayments: {
        id: expectedpaymentid;
        amount: currency;
        date: joda.LocalDate;
        periodic_payment_method: periodic_payment_method | null;
      }[];
    };
    newInvoice: {
      expectedpayments: {
        amount: currency;
        date: joda.LocalDate;
        periodic_payment_method: periodic_payment_method | null;
      }[];
    };
  },
) {
  for (const currep of opts.currentInvoice.expectedpayments) {
    if (
      opts.newInvoice.expectedpayments.some((newep) =>
        epsAreEqual(currep, newep),
      )
    ) {
      // existing ep is still there
    } else {
      await expected_payments.del(db, { id: currep.id });
    }
  }

  for (const newep of opts.newInvoice.expectedpayments) {
    if (
      opts.currentInvoice.expectedpayments.some((currep) =>
        epsAreEqual(currep, newep),
      )
    ) {
      // new ep already exists
    } else {
      await expected_payments.insert(db, {
        amount: newep.amount,
        date: newep.date,
        last_reminder_sent: null,
        invoiceid: opts.currentInvoice.id,
        pay_during_online_enrollment: false, // not relevant anymore, enrollment is long done
        periodic_payment_method: newep.periodic_payment_method,
      });
    }
  }

  function epsAreEqual(
    currep: {
      amount: currency;
      date: joda.LocalDate;
      periodic_payment_method: periodic_payment_method | null;
    },
    newep: {
      amount: currency;
      date: joda.LocalDate;
      periodic_payment_method: periodic_payment_method | null;
    },
  ): boolean {
    return (
      newep.amount.value === currep.amount.value &&
      newep.date.isEqual(currep.date) &&
      newep.periodic_payment_method === currep.periodic_payment_method
    );
  }
}

export async function getInvoiceSubjects(
  db: connection,
  invs:
    | { tag: "id"; ids: invoiceid[] }
    | {
        tag: "invoice";
        invoices: {
          invoiceid: invoiceid;
          enrollmentid: enrollmentid | null;
          enrollmentrequestid: enrollmentrequestid | null;
          student_period_fee_id: student_period_fee_id | null;
          student_charge_id: student_charge_id | null;
        }[];
      },
): Promise<invoiceSubject[]> {
  const invoices =
    invs.tag === "invoice"
      ? invs.invoices
      : await get_invoices_by_id(db, { ids: invs.ids });
  const enrollmentids = mapPartial(invoices, (inv) => inv.enrollmentid);
  const from_enrs =
    enrollmentids.length === 0
      ? []
      : await get_enrollments_for_student_by_ids(db, { enrollmentids }).then(
          (enrs) =>
            enrs.map(
              (enr): invoiceSubject => ({
                tag: "enrollment" as const,
                invoiceid: invoices.find((inv) => inv.enrollmentid === enr.id)!
                  .invoiceid,
                lg: enr.lessongroup,
                teacher: enr.teacher,
                student: enr.student,
                created: enr.created,
                id: enr.id,
                period: enr.period,
                status: enr.status,
                instrument: enr.instrument,
                room_description: enr.room_description,
              }),
            ),
        );

  const studentPeriodFeeIds = mapPartial(
    invoices,
    (inv) => inv.student_period_fee_id,
  );

  const from_pfs =
    studentPeriodFeeIds.length === 0
      ? []
      : await get_periodfees(db, { ids: studentPeriodFeeIds }).then((pfs) =>
          pfs.map(
            (pf): invoiceSubject => ({
              tag: "periodfee" as const,
              invoiceid: invoices.find(
                (inv) => inv.student_period_fee_id === pf.id,
              )!.invoiceid,
              id: pf.id,
              created: pf.created,
              student: pf.student,
              period: pf.period,
            }),
          ),
        );

  const enrollmentrequestids = mapPartial(
    invoices,
    (inv) => inv.enrollmentrequestid,
  );

  if (enrollmentrequestids.length > 1) {
    // TODO: If we use this for more than one enr_req, we need to optimize this!
    throw new Error(`Not implemented yet: multiple enrollmentrequestids`);
  }

  const from_enr_reqs =
    enrollmentrequestids.length === 0
      ? []
      : await Promise.all(
          enrollmentrequestids.map((id) =>
            getEnrollmentrequestFull(db, {
              enrollmentrequestid: id,
            }).then(
              (enr_req): invoiceSubject => ({
                tag: "enrollmentrequest" as const,
                created: enr_req.created.toLocalDate(),
                student: enr_req.student,
                period: enr_req.period,
                invoiceid: invoices.find(
                  (inv) => inv.enrollmentrequestid === enr_req.id,
                )!.invoiceid,
                enr_req,
              }),
            ),
          ),
        );

  const studentChargeIds = mapPartial(invoices, (inv) => inv.student_charge_id);

  const from_student_charges =
    studentChargeIds.length === 0
      ? []
      : await get_student_charges(db, { ids: studentChargeIds }).then((scs) =>
          scs.map(
            (sc): invoiceSubject => ({
              tag: "student_charge",
              invoiceid: invoices.find(
                (inv) => inv.student_charge_id === sc.id,
              )!.invoiceid,
              created: sc.student_charge.created
                .atZone(joda.ZoneId.UTC /* TODO */)
                .toLocalDate(),
              student: sc.student,
              student_charge: sc.student_charge,
              period: sc.period,
            }),
          ),
        );

  const res: invoiceSubject[] = [
    ...from_enrs,
    ...from_pfs,
    ...from_enr_reqs,
    ...from_student_charges,
  ];

  if (res.length !== invoices.uniqBy((i) => i.invoiceid).length) {
    throw new Error("getInvoiceSubjects: length is not equal");
  }

  return res;
}

export function calculateAmountWithoutVat(il: {
  amount_with_vat: currency;
  amount_without_vat: currency | null;
  vat: number;
}): currency {
  return (
    il.amount_without_vat ?? il.amount_with_vat.divide((il.vat + 100) / 100)
  );
}

export async function getInvoiceLinesForLessongroup(
  db: connection,
  opts: {
    trial_lesson: boolean;
    lessongroupid: lessongroupid;
    formula: {
      trial_lesson_price: currency;
      trial_lesson_vat: number;
    };
  },
): Promise<
  {
    type_: invoicelinetype;
    amount_with_vat: price_adapted_to_lg;
    amount_without_vat: price_adapted_to_lg | null;
    vat: number;
    description: string;
    chargemode: formulachargemode;
  }[]
> {
  const ils =
    opts.trial_lesson === true
      ? opts.formula.trial_lesson_price.intValue > 0
        ? [
            {
              amount_with_vat: opts.formula
                .trial_lesson_price as price_adapted_to_lg,
              amount_without_vat: null,
              vat: opts.formula.trial_lesson_vat,
              description: "Proefles",
              type_: "Normal" as const,
              chargemode: "Enrollment" as const,
            },
          ]
        : []
      : await get_invoicelines_for_lessongroup(db, {
          lessongroup_id: opts.lessongroupid,
        }).then((ils) =>
          ils.map((il) => ({
            ...il,
            type_: parseInvoicelineType(il.type_),
          })),
        );

  return ils;
}
